import * as React from "react";
import {useEffect, useState} from "react";
import {Button, Header, Icon, Loader, Segment, Tab} from "semantic-ui-react";
import {authentication} from "../../../../../authentication";
import {backend} from "../../../../../xconvert-backend";
import {notify} from 'react-notify-toast';
import {initialSettings, OptimizeJsonSettings, TransportOrderJsonViewer} from "./TransportOrderJsonViewer";

export interface MultiTransportOrderJsonViewerProps {
    transportOrderIds: string[];
    onBack: () => void;
}

export function MultiTransportOrderJsonViewer(props: MultiTransportOrderJsonViewerProps) {
    const [transportOrders, setTransportOrders] = useState<Array<any & {
        originalData?: any,
        isOptimized?: boolean,
        currentOptimizeSettings?: OptimizeJsonSettings
    }>>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);


    useEffect(() => {
        async function fetchAndSetTransportOrders() {
            setLoading(true);
            setError(null);
            try {
                const auth = backend.withTokenAuthHeader(authentication.token);
                const ordersPromises = props.transportOrderIds.map(async (orderId) => {
                    try {
                        const response = await backend.transportOrderApi.fetchTransportOrderById(orderId, auth);
                        return {
                            id: orderId,
                            data: response,
                            originalData: JSON.parse(JSON.stringify(response)),
                            isOptimized: false,
                            currentOptimizeSettings: {...initialSettings},
                            error: null
                        };
                    } catch (err: any) {
                        return {
                            id: orderId,
                            data: null,
                            originalData: null,
                            isOptimized: false,
                            currentOptimizeSettings: {...initialSettings},
                            error: `Failed to load order: ${err.message || 'Unknown error'}`
                        };
                    }
                });
                const orders = await Promise.all(ordersPromises);
                setTransportOrders(orders);

            } catch (err: any) {
                setError("Failed to load transport orders");
            } finally {
                setLoading(false);
            }
        }

        if (props.transportOrderIds && props.transportOrderIds.length > 0) {
            fetchAndSetTransportOrders().then(r => {
            });
        } else {
            setTransportOrders([]);
            setError("No transport order IDs provided");
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.transportOrderIds]);

    if (loading) {
        return <Segment basic>
            <Loader active>Loading...</Loader>
        </Segment>;
    }

    if (error && !transportOrders.length) {
        return <Segment color="red">
            <Header as="h3">Error</Header>
            <p>{error}</p>
            <Button onClick={props.onBack}>Back</Button>
        </Segment>;
    }

    if (!transportOrders.length && !loading) {
        return <Segment placeholder>
            <Header icon>
                <Icon name="file outline"/>
                {error || "No orders."}
            </Header>
            <Button
                primary
                onClick={props.onBack}
            >
                Back
            </Button>
        </Segment>;
    }

    const panes = transportOrders.map((order, index) => ({
        menuItem: {
            key: order.id,
            content: (
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <span style={{marginRight: '0.4em'}}>{order.id.substring(0, 10)}</span>
                    <Button
                        icon="copy"
                        size="mini"
                        basic
                        compact
                        title="Copy full ID"
                        onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(order.id);
                            notify.show('ID copied to clipboard', 'success', 1500);
                        }}
                        style={{padding: '0.25em', height: '1.6em'}}
                    />
                </div>
            ),
        },
        render: () => (
            <Tab.Pane
                key={order.id}
                attached={false}
                style={{
                    padding: '0.5em',
                    border: 'none',
                    background: 'transparent',
                    display: 'flex',
                    flex: 1,
                }}>
                <TransportOrderJsonViewer
                    key={order.id}
                    transportOrder={JSON.stringify(order.data, null, 4)}
                    readOnly={false}
                    onChange={() => {
                    }}
                />
            </Tab.Pane>
        ),
    }));


    return (
        <>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '0.75em',
                paddingBottom: '0.5em',
                borderBottom: '1px solid #ddd'
            }}>
                <Button
                    icon
                    labelPosition="left"
                    onClick={props.onBack}
                    size="small"
                >
                    <Icon name="arrow left"/>Back to Tasks
                </Button>
                <Header
                    as="h3"
                    style={{
                        margin: 0,
                        color: '#333'
                    }}>
                    Transport Order Viewer <span
                    style={{fontSize: '0.8em', color: '#666'}}>({transportOrders.length} loaded)</span>
                </Header>
            </div>
            {transportOrders.length > 0 ? (
                <Tab
                    style={{
                        flex: 1,
                        minHeight: '400px',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                    menu={{
                        secondary: true,
                        pointing: true,
                        style: {
                            marginBottom: '0.5em',
                            fontSize: '0.95em'
                        }
                    }}
                    panes={panes}
                    activeIndex={activeIndex}
                    onTabChange={(_, data) => setActiveIndex(data.activeIndex as number)}
                />
            ) : (!loading &&
                <Segment placeholder>
                    <Header icon>
                        <Icon name="file outline"/>No orders to display.
                    </Header>
                </Segment>
            )}
        </>
    );
}